import { Injectable } from '@angular/core'
import { LocalizationService } from '@app-domains/localization/service/localization.service'
import {
    LocalesEnum,
    PublishedEnum,
    SimpleThemeFragment,
    ThemeFragment,
    ThemeQueryService,
    ThemesQueryService,
} from '@app-graphql/schema'
import { combineLatestWith, firstValueFrom, from, Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { locales, Localization } from '@aa-app-localization'
import { LocalizedRouting } from '@app-domains/navigation/types/navigation.types'

@Injectable({
    providedIn: 'root',
})
export class ThemesService {
    private themes: readonly SimpleThemeFragment[]

    constructor(
        public readonly localization: LocalizationService,
        public readonly themesQueryService: ThemesQueryService,
        public readonly themeQueryService: ThemeQueryService,
    ) {
    }

    public all(): readonly SimpleThemeFragment[] {
        return this.themes
    }

    public async initialize(): Promise<void> {
        this.themes = await this.fetchThemes()

        type T = Record<Localization.Locale, Record<string, string>>

        const xs = this.themes.reduce<T>((acc, cat) => {
            for (const locale of this.localization.langList) {
                acc[locale][`theme-${ cat.id }`] = cat.translations[locale]!.slug
            }
            return acc
        }, {
            en: {},
            nl: {},
            de: {},
            fr: {},
        })

        await Promise.all(locales.map((locale) => this.localization.patchTranslations(locale, {
            ROUTES: xs[locale],
        })))
    }

    public findBySlug(slug: string): SimpleThemeFragment | null {
        const themes = this.all()
        const locale = this.localization.getCurrentLocale()
        return themes.find((x: SimpleThemeFragment) => slug === x.translations[locale]!.slug) ?? null
    }

    private async fetchThemes(): Promise<SimpleThemeFragment[]> {
        const themes = await firstValueFrom(this.themesQueryService
            .fetch({
                variables: {
                    locale: this.localization.getCurrentLocale() as LocalesEnum,
                    published: PublishedEnum.IsPublished,
                },
            })
            .pipe(
                map(({ data }) => data?.themes ?? []),
            ))

        return themes?.length ? themes : []
    }

    public fetchTheme(slug: string, locale: LocalesEnum): Observable<ThemeFragment> {
        return this.themeQueryService.fetch({
            variables: {
                slug,
                locale,
            },
            fetchPolicy: 'no-cache',
        }).pipe(
            map(({ data }) => data?.theme),
            filter((theme): theme is ThemeFragment => !! theme),
        )
    }

    public getTranslatedThemes(): Observable<LocalizedRouting[]> {
        return from(this.fetchThemes()).pipe(
            combineLatestWith(this.localization.currentLocale$),
            map(([themes, lang]) => themes.map(
                (theme: SimpleThemeFragment) => this.translateThemes(theme, lang)),
            ),
        )
    }

    private translateThemes(theme: SimpleThemeFragment, lang: Localization.Locale): LocalizedRouting {
        const localization = theme.translations[lang]
        return {
            id: theme.id.toString(),
            name: localization?.title ?? '',
            slug: localization?.slug ?? '',
            // eslint-disable-next-line max-len
            highlightedItems: localization?.highlightedItem?.filter((item): item is NonNullable<typeof item> => !! item) ?? [],
        }
    }

}
